import { CommonModule, DOCUMENT } from '@angular/common'
import { HttpClient, HttpHeaders, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http'
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, inject, Input, Output, ViewEncapsulation } from '@angular/core';
import { NgElement, WithProperties } from '@angular/elements'
import { registerCustomElementFactory } from '../../custom-elements/register-custom-element-factory'
import { AsyncActionState } from '../async-action-state'
import { ActionEvent, ActionEventData, ActionCompletedEventData, ActionFailedEventData } from './action-event'
import { ActionOptions } from './action-options'
import { ActionParamEncodingType } from './action-param-encoding-type'
import { unflatten } from '../../util'
import { filter, map } from 'rxjs/operators'

/**
 * @public
 */
export type ActionElement = NgElement & WithProperties<ActionComponent>

/**
 * A component that can be used to start an asynchronous action upon click.
 *
 * It's default behavious is to make an HTTP request to the provided URL and wait for a response.
 *
 * @public
 */
@Component({
  selector: 'gpei-action',
  templateUrl: './action.component.html',
  styleUrls: ['./action.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: true,
  imports: [
    CommonModule
  ]
})
export class ActionComponent {

  /**
   * The HTTP method used to perform the action
   */
  @Input()
  @HostBinding()
  method = 'GET'

  /**
   * The target URL that will be called for the action
   */
  @Input()
  @HostBinding()
  url: string

  /**
   * The encoding format used for the parameters of the action.
   *
   * When {@link method} is set to 'GET', the only possible option is 'application/x-www-form-urlencoded'.
   */
  @Input()
  @HostBinding()
  formenctype: ActionParamEncodingType = 'application/x-www-form-urlencoded'

  /**
   * Event emitter that is fired when the action is started
   */
  @Output() started: EventEmitter<ActionEventData> = new EventEmitter()

  /**
   * Event emitter that is fired when the action is completed
   */
  @Output() completed: EventEmitter<ActionEventData> = new EventEmitter()

  /**
   * Event emitter that is fired when the action encountered an error
   */
  @Output() failed: EventEmitter<ActionEventData> = new EventEmitter()

  /**
   * @internal
   */
  AsyncActionState = AsyncActionState

  protected _value: unknown

  protected readonly STARTED_EVENT_TYPE: string = 'actionstart'
  protected readonly COMPLETED_EVENT_TYPE: string = 'actioncomplete'
  protected readonly FAILED_EVENT_TYPE: string = 'actionfailure'

  protected _http = inject(HttpClient)
  protected _changeDetectorRef = inject(ChangeDetectorRef)
  protected _document = inject(DOCUMENT)
  protected _elementRef = inject(ElementRef<HTMLElement>)

  private _state: AsyncActionState = AsyncActionState.IDLE
  private _for: string
  private _paramsElement: HTMLElement

  /**
   * Lifecycle hook for when the action button is clicked.
   *
   * It will request that the action is started.
   */
  onActionButtonClick(): void {
    this.requestStart()
  }

  /**
   * Lifecycle hook for when the execution of the action is complete.
   *
   * It will emit the value through both an `actioncomplete` DOM event, and the {@link ActionComponent.completed} event emitter.
   * Then if the default behaviour is not prevented, it will set the value of the element to the response of the action,
   * and set the state of the element to {@link AsyncActionState.COMPLETE}.
   *
   * @param value The response value resolved from the action.
   */
  onActionComplete(value: unknown, context?: ActionOptions): void {
    // Set up the data for the events
    let eventData: ActionCompletedEventData = { value }

    // If the context does not contain a promise
    if (!(context?.fromPromise instanceof Promise)) {
      eventData.method = context?.method ?? this.method
      eventData.url = context?.url ?? this.url
      eventData.encoding = context?.encoding ?? this.formenctype
      eventData.params = context?.params ?? this._params
    }

    // Emit the event via Angular output and native DOM event
    this.completed.emit(eventData)
    let continued = this.dispatchEvent(this.COMPLETED_EVENT_TYPE, eventData)

    // If the native event was not cancelled, perform the default behaviour
    if (continued) {
      // Set the state to complete
      this.state = AsyncActionState.COMPLETE

      // Set the response from the action as the value of the element
      this._value = value
    }
  }

  /**
   * Lifecycle hook for when the execution of the action encounters an error.
   *
   * It will emit the error through both an `actioncomplete` DOM event, and the {@link ActionComponent.failed} event emitter.
   * Then if the default behaviour is not prevented it will set the state of the element to {@link AsyncActionState.ERROR}.
   *
   * @param error The error encountered during the execution of the action.
   */
  onActionError(error: Error, context?: ActionOptions): void {
    // Set up the data for the events
    let eventData: ActionFailedEventData = { error }

    // If the context does not contain a promise
    if (!(context?.fromPromise instanceof Promise)) {
      eventData.method = context?.method ?? this.method
      eventData.url = context?.url ?? this.url
      eventData.encoding = context?.encoding ?? this.formenctype
      eventData.params = context?.params ?? this._params
    }

    // Emit the event via Angular output and native DOM event
    this.failed.emit(eventData)
    let continued = this.dispatchEvent(this.FAILED_EVENT_TYPE, eventData)

    // If the native event was not cancelled, perform the default behaviour
    if (continued) {
      // Set the state to error
      this.state = AsyncActionState.ERROR

      // Reset the value of the element
      this._value = null
    }
  }

  /**
   * Request that the execution of the action is started.
   *
   * It will emit both the `actionstart` DOM event, and the {@link ActionComponent.started} event emitter.
   * Then if the default behaviour is not prevented, it will either make an HTTP request to the provided URL,
   * or it will evaluate the provided promise.
   *
   * @param args  Configure the url, and method, or target promise for the action.
   *
   * @returns     The response of the asynchronous action. In the event that an error is encountered while
   *              the action is being executed `NULL` will be returned.
   */
  @Input()
  @HostBinding()
  async requestStart(args?: ActionOptions): Promise<unknown> {
    // If a request is currently active, exit
    if (this.state === AsyncActionState.ACTIVE) {
      return null
    }

    // Set up the data for the events
    let eventData: ActionEventData = {}

    // If the args do not contain a promise
    if (!(args?.fromPromise instanceof Promise)) {
      eventData.method = args?.method ?? this.method
      eventData.url = args?.url ?? this.url
      eventData.encoding = args?.encoding ?? this.formenctype
      eventData.params = args?.params ?? this._params
    }

    // Emit the event via Angular output and native DOM event
    this.started.emit(eventData)
    let continued = this.dispatchEvent(this.STARTED_EVENT_TYPE, eventData)

    // If the native event was not cancelled, perform the default behaviour
    if (continued) {
      return await this.start(args)
    }

    return null
  }

  /**
   * Start the execution of the action.
   *
   * @param args  Configure the url, and method, or target promise for the action.
   *
   * @returns     The response of the asynchronous action. In the event that an error is encountered while
   *              the action is being executed `NULL` will be returned.
   */
  @Input()
  @HostBinding()
  async start(args?: ActionOptions): Promise<unknown> {
    // Toggle the active state
    this.state = AsyncActionState.ACTIVE

    // Get the asynchronous request
    let request = args?.fromPromise instanceof Promise ? args?.fromPromise : this.sendHttpRequest(args)

    // Await the response
    try {
      let response = await request
      this.onActionComplete(response, args)
      return response
    }

    // Catch any errors thrown while while making the HTTP request
    catch (e) {
      this.onActionError(e, args)
      return null
    }
  }

  /**
   * Encode the parameters of a URL based action into the provided format.
   *
   * If the parameters are provided as a {@link FormData}, {@link URLSearchParams}, or `object`,
   * then it will be converted into the requested format. In the event that a `string` or any value other than `NULL` is provided,
   * then it will return the provided value in the format of a `string`.
   *
   * @param contentType The mimetype of the format that the params will be coerced into.
   * @param params      The parameters of the action.
   *
   * @returns           The encoded parameters in the requested format.
   */
  @Input()
  @HostBinding()
  encodeParams(contentType: string, params: unknown): FormData | URLSearchParams | string {
    let formattedContentType = contentType.toLowerCase()

    // Bail out if the params are null or a string
    if (params == null || typeof params === 'string') {
      return params as string
    }

    // Bail out if the params are already in the required format
    if (params instanceof FormData && formattedContentType === 'multipart/form-data') {
      return params
    }

    if (params instanceof URLSearchParams && formattedContentType === 'application/x-www-form-urlencoded') {
      return params
    }

    // Handle the conversion
    if (params instanceof FormData || params instanceof URLSearchParams || typeof params === 'object') {
      // Convert the FormData or URLSearchParams into a more maliable object
      let pendingParams = !(params instanceof FormData || params instanceof URLSearchParams) ?
        params :
        Object.fromEntries(params.entries())

      // Perform the conversion
      switch (formattedContentType) {
        case 'multipart/form-data': {
          let formData = new FormData()
          for (let key in pendingParams) {
            formData.append(key, pendingParams[key])
          }
          return formData
        }

        case 'application/json': {
          pendingParams = unflatten(pendingParams as Record<string, unknown>)
          return JSON.stringify(pendingParams)
        }

        case 'application/x-www-form-urlencoded': {
          return new URLSearchParams(pendingParams as Record<string, string>)
        }
      }
    }

    // If nothing matched the expected format, convert it to a string
    return JSON.stringify(params)
  }

  /**
   * Create an HTTP request that will be sent by the action.
   *
   * @param args  The arguments used to create the request. If any of the options are missing,
   *              then the matching values of the element will be used as a default.
   *
   * @returns     HTTP request that can be sent via Angular's {@link HttpClient}.
   */
  @Input()
  @HostBinding()
  createHttpRequest(args: ActionOptions): HttpRequest<unknown> {
    // Expand the arguments and get any defaults
    let method = args?.method ?? this.method
    let url = args?.url ?? this.url
    let encoding = args?.encoding ?? this.formenctype
    let params = args?.params ?? this._params

    let body = null
    let httpParams = null
    let headers = new HttpHeaders()

    // Encode the parameters
    if (params) {
      let encodedParams = this.encodeParams(encoding, params)

      // Add the params to the request options
      if (encodedParams instanceof URLSearchParams) {
        httpParams = new HttpParams({ fromString: encodedParams.toString() })
      }

      else {
        body = encodedParams
        headers = headers.append('Content-Type', encoding)
      }
    }

    return new HttpRequest(method, url, body, { headers, params: httpParams, withCredentials: true })
  }

  /**
   * Send an HTTP request for the action.
   *
   * @param args  The arguments that will be used to populate an HTTP request.
   *
   * @returns     An asynchronous response from the requested URL.
   */
  protected async sendHttpRequest(args: ActionOptions): Promise<unknown> {
    let request = this.createHttpRequest(args)
    return this._http
      .request(request)
      .pipe(
        filter(it => it instanceof HttpResponse),
        map((it: HttpResponse<unknown>) => it.body)
      )
      .toPromise()
  }

  /**
   * Dispatch a native DOM event of the provided type and with the provided data.
   *
   * @param type  The type of event emitted.
   * @param data  The initial data for the event.
   *
   * @returns     `TRUE`, else it will return `FALSE` if the event's default behaviour is prevented.
   */
  protected dispatchEvent(type: string, data: ActionEventData | ActionCompletedEventData | ActionFailedEventData): boolean {
    let event = new ActionEvent(type, {
      bubbles: true,
      cancelable: true,
      ...data
    })
    return this._elementRef.nativeElement.dispatchEvent(event)
  }

  /**
   * The current state of the element.
   *
   * The following are the states that it can be in:
   *
   * | State                              | Description                                                                                             |
   * |------------------------------------|---------------------------------------------------------------------------------------------------------|
   * | {@link AsyncActionState.IDLE}      | The element is not running the action, and will run the action upon click.                              |
   * | {@link AsyncActionState.ACTIVE}    | The element is currently running the action, and nothing will happen upon click.                        |
   * | {@link AsyncActionState.COMPLETE}  | The element has completed at least one action, and will run the action again upon click.                |
   * | {@link AsyncActionState.ERROR}     | The element had encountered an error during the last action, and will run the action again upon click.  |
   */
  @Input()
  @HostBinding()
  set state(value: string | AsyncActionState) {
    let pendingValue

    // If the value is a string
    if (typeof value === 'string') {
      let enumKey = Object.keys(AsyncActionState)
        .find(it => it.toLowerCase() === value.toLocaleLowerCase())
      pendingValue = AsyncActionState[enumKey]
    }

    else {
      pendingValue = value
    }

    this._state = pendingValue
    this._changeDetectorRef.detectChanges()
  }

  get state(): AsyncActionState {
    return this._state
  }

  /**
   * The response of the most recently run action.
   *
   * If an action fails to execute the value will be reset to `NULL`.
   */
  @HostBinding()
  get value(): unknown {
    return this._value
  }

  /**
   * The value of the `part` attribute for the action button
   */
  get actionButtonPart(): string {
    let defaultPart = 'button'

    switch (this.state) {
      case AsyncActionState.ACTIVE: {
        return `${ defaultPart } button-active`
      }

      case AsyncActionState.COMPLETE: {
        return `${ defaultPart } button-complete`
      }

      case AsyncActionState.ERROR: {
        return `${ defaultPart } button-failure`
      }

      default: {
        return defaultPart
      }
    }
  }

  /**
   * The id of a target form that can act as a source of data for the action
   */
  @Input()
  @HostBinding()
  set for(value: string) {
    this._for = value

    if (this._for) {
      this._paramsElement = this._document.querySelector(`#${this.for}`)
    }
  }

  get for(): string {
    return this._for
  }

  /**
   * The element that is currently assigned to provide parameters for the action
   */
  @HostBinding()
  get paramsElement(): HTMLElement {
    return this._paramsElement
  }

  /**
   * The parameters from the {@link paramsElement}.
   *
   * These parameters are retrieved from the {@link paramsElement} in the form of {@link FormData}
   * of the element if it is an {@link HTMLFormElement}, or from the `value` property on the element if it exists.
   * As such, this element can be something like a form, a single hidden input, or a custom element.
   */
  protected get _params(): unknown {
    if (this._paramsElement) {
      if (this._paramsElement instanceof HTMLFormElement) {
        return new FormData(this._paramsElement)
      }

      if ('value' in this._paramsElement) {
        return this._paramsElement.value
      }
    }

    return null
  }

}

/**
 * @public
 */
export const registerActionElement = registerCustomElementFactory(ActionComponent)
